import React, { useState } from 'react';
import { Row, Col, Collapse } from 'reactstrap';
import { toast } from 'react-toastify';
import MemberMembershipService from '../../../../../../services/member_membership_service';
import ConfirmDialog from '../../../../../../components/Common/ConfirmDialog';
import { NeoButton, NeoBadge, NeoInput, NeoDatePicker } from '../../../../../../components/Common';
import { formatCurrency } from '../../../../../../utils/format';
import { toISODate, formatDate } from '../../../../../../utils/date';
import { storage } from '../../../../../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const ActionSuspend = ({ membership, idTenant, user, onRefresh }) => {
    const [suspendEndDate, setSuspendEndDate] = useState("");
    const [isScheduledSuspend, setIsScheduledSuspend] = useState(false);
    const [suspendStartDate, setSuspendStartDate] = useState("");
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [uploading, setUploading] = useState(false);

    const freezes = membership.freezes || [];
    const isSuspended = membership.status === "suspended";

    const handleSuspend = async () => {
        if (!suspendEndDate) return toast.warning("Informe a data de término da suspensão.");
        if (isScheduledSuspend && !suspendStartDate) return toast.warning("Informe a data de início da suspensão agendada.");

        const startDate = isScheduledSuspend ? suspendStartDate : toISODate(new Date());
        const sTime = new Date(startDate + "T00:00:00").getTime();
        const eTime = new Date(suspendEndDate + "T00:00:00").getTime();

        if (eTime <= sTime) return toast.warning("Data final deve ser posterior à data inicial.");

        const n = Math.round((eTime - sTime) / (1000 * 60 * 60 * 24));

        const ok = await ConfirmDialog({
            title: isScheduledSuspend ? "Agendar Suspensão?" : "Suspender plano?",
            text: isScheduledSuspend
                ? `O plano ficará suspenso do dia ${formatDate(startDate)} até ${formatDate(suspendEndDate)} (${n} dias).`
                : `O plano será suspenso a partir de hoje até ${formatDate(suspendEndDate)} (${n} dias).`,
            confirmButtonText: "Sim, confirmar",
        });
        if (!ok) return;

        setLoading(true);
        let attachmentUrl = null;
        let attachmentName = null;

        try {
            if (attachment) {
                setUploading(true);
                const fileExt = attachment.name.split('.').pop() || "jpg";
                const storagePath = `tenants/${idTenant}/members/${membership.idMember}/suspensions/${membership.id}_${Date.now()}.${fileExt}`;
                const fileRef = ref(storage, storagePath);
                await uploadBytes(fileRef, attachment);
                attachmentUrl = await getDownloadURL(fileRef);
                attachmentName = attachment.name;
            }

            await MemberMembershipService.suspend(membership.id, idTenant, user, membership, {
                days: n,
                reason: reason || "Suspensão pelo gestor",
                startDate: startDate,
                attachmentUrl,
                attachmentName
            });
            toast.success(isScheduledSuspend ? "Suspensão agendada." : "Plano suspenso.");
            setSuspendEndDate("");
            setSuspendStartDate("");
            setIsScheduledSuspend(false);
            setReason("");
            setAttachment(null);
            onRefresh?.();
        } catch (err) {
            console.error("Erro ao suspender:", err);
            toast.error("Erro ao suspender.");
        } finally {
            setLoading(false);
            setUploading(false);
        }
    };

    return (
        <div className="p-4 border mt-3 bg-white shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(0, 0, 0, 0.06)', fontFamily: 'Outfit, sans-serif' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <i className="mdi mdi-pause-circle-outline fs-4 me-2 text-muted" style={{ color: 'var(--color-brand-secondary) !important' }}></i>
                    <h6 className="fw-bold mb-0 text-uppercase" style={{ letterSpacing: "0.5px", color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                        Suspender Plano
                    </h6>
                </div>
                <div className="form-check form-switch font-size-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <input className="form-check-input" type="checkbox" role="switch" id="switchSchedSuspend"
                        checked={isScheduledSuspend} onChange={(e) => setIsScheduledSuspend(e.target.checked)} />
                    <label className="form-check-label text-muted ms-1" htmlFor="switchSchedSuspend">Agendar futuro?</label>
                </div>
            </div>

            <p className="text-muted font-size-13 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                O trancamento (suspensão) paralisa a contagem do tempo do contrato. O tempo paralisado será adicionado ao final da vigência do aluno quando o período de suspensão acabar.
            </p>

            {membership.allowsSuspension && (
                <div className="d-flex align-items-center bg-white border p-3 mb-4 shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(72, 39, 18, 0.08)', fontFamily: 'Outfit, sans-serif' }}>
                    <i className="mdi mdi-snowflake fs-5 text-muted mx-2"></i>
                    <div className="flex-grow-1 font-size-12 text-muted">
                        Dias disponíveis no contrato: <strong className="text-dark">{membership.disponibleSuspensionDays || 0}</strong> de {membership.maxSuspensionDays || 0}
                    </div>
                    {membership.suspensionFee > 0 && (
                        <div className="font-size-12 ms-3 pe-2 text-muted">
                            Taxa de bloqueio: <strong className="text-dark">{formatCurrency(membership.suspensionFee)}</strong>
                        </div>
                    )}
                </div>
            )}

            <Row className="g-3 align-items-start" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Col md={3}>
                    <NeoDatePicker
                        label="Data Início"
                        value={isScheduledSuspend ? suspendStartDate : toISODate(new Date())}
                        onChange={date => setSuspendStartDate(date)}
                        disabled={!isScheduledSuspend}
                        size="sm"
                    />
                </Col>
                <Col md={3}>
                    <NeoDatePicker
                        label="Data Fim"
                        value={suspendEndDate}
                        onChange={date => setSuspendEndDate(date)}
                        size="sm"
                    />
                </Col>
                <Col md={6}>
                    <NeoInput
                        label="Motivo (Opcional)"
                        placeholder="Viagem, saúde..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        size="sm"
                    />
                </Col>
            </Row>

            <Row className="g-3 align-items-start mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Col md={12}>
                    <div className="font-size-12 fw-bold text-muted mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Atestado Médico / Documento (Opcional - Apenas Imagens)</div>
                    <div className="d-flex align-items-center gap-3">
                        <label className="btn btn-sm btn-outline-dark mb-0 d-inline-flex align-items-center gap-2" style={{ cursor: 'pointer', height: '36px', borderRadius: '8px' }}>
                            <i className="mdi mdi-upload font-size-16"></i>
                            {uploading ? "Enviando..." : "Selecionar Foto / Imagem"}
                            <input
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setAttachment(file);
                                }}
                                disabled={uploading || loading}
                            />
                        </label>
                        {attachment && (
                            <div className="d-flex align-items-center gap-2 bg-white border p-2 py-1 font-size-12 shadow-sm" style={{ borderRadius: '8px', borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                                <i className="mdi mdi-paperclip text-muted"></i>
                                <span className="text-dark fw-medium text-truncate" style={{ maxWidth: '200px' }}>{attachment.name}</span>
                                <button
                                    type="button"
                                    className="btn btn-sm text-danger p-0 border-0 ms-2"
                                    onClick={() => setAttachment(null)}
                                    disabled={uploading || loading}
                                >
                                    <i className="mdi mdi-trash-can-outline font-size-16"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4">
                <NeoButton
                    variant="primary"
                    onClick={handleSuspend}
                    loading={loading || uploading}
                    size="sm"
                    className="px-4"
                >
                    Confirmar Suspensão
                </NeoButton>
            </div>

            {/* ── Menu Colapsável: Histórico de Suspensões ── */}
            {freezes.length > 0 && (
                <div className="mt-4 pt-3 border-top" style={{ borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                    <NeoButton
                        variant="outline-secondary"
                        className="w-100 d-flex justify-content-between align-items-center px-3 py-2 text-decoration-none"
                        onClick={() => setShowHistory(!showHistory)}
                        size="sm"
                    >
                        <span className="d-flex align-items-center gap-2 font-size-12 fw-bold text-dark">
                            <i className="mdi mdi-history font-size-16 text-muted"></i>
                            Histórico de Suspensões
                            <NeoBadge color="muted" className="font-size-10 px-2">
                                {freezes.length}
                            </NeoBadge>
                        </span>
                        <i className={`mdi ${showHistory ? "mdi-chevron-up" : "mdi-chevron-down"} font-size-18 text-muted`}></i>
                    </NeoButton>

                    <Collapse isOpen={showHistory}>
                        <div className="d-flex flex-column gap-2 mt-2" style={{ maxHeight: "300px", overflowY: "auto" }}>
                            {[...freezes].reverse().map((freeze, idx) => {
                                const originalIdx = freezes.length - 1 - idx;
                                const isLastFreeze = originalIdx === freezes.length - 1;
                                const todayStr = new Date().toISOString().split("T")[0];

                                // Calcular data fim da suspensão
                                const endDateObj = new Date(freeze.startDate + "T00:00:00");
                                endDateObj.setDate(endDateObj.getDate() + freeze.days);
                                const computedEndDate = endDateObj.toISOString().split("T")[0];

                                // Determinar status da suspensão
                                let freezeStatus = "concluded";
                                let statusColor = "success";
                                let statusIcon = "mdi-check-circle";
                                let statusLabel = "Concluída";

                                if (isLastFreeze && isSuspended) {
                                    freezeStatus = "active";
                                    statusColor = "warning";
                                    statusIcon = "mdi-pause-circle";
                                    statusLabel = "Ativa";
                                } else if (freeze.startDate > todayStr) {
                                    freezeStatus = "scheduled";
                                    statusColor = "info";
                                    statusIcon = "mdi-clock-outline";
                                    statusLabel = "Agendada";
                                }

                                const refunded = freeze.refundedDays || 0;
                                const displayEndDate = freeze.endDate || computedEndDate;

                                return (
                                    <div
                                        key={idx}
                                        className="border p-3 position-relative shadow-sm"
                                        style={{
                                            borderRadius: '12px',
                                            backgroundColor: freezeStatus === "active" ? "rgba(255,193,7,0.04)" : "#ffffff",
                                            borderLeftWidth: "3px",
                                            borderLeftStyle: "solid",
                                            borderLeftColor: freezeStatus === "active" ? "#ffc107" : freezeStatus === "scheduled" ? "#50a5f1" : "#34c38f"
                                        }}
                                    >
                                        {/* Linha 1: Número + Status + Dias */}
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="font-size-12 fw-bold text-dark">
                                                    #{freezes.length - idx}
                                                </span>
                                                <NeoBadge color={statusColor} icon={statusIcon} className="font-size-10 px-2 py-0">
                                                    {statusLabel}
                                                </NeoBadge>
                                            </div>
                                            <span className="font-size-12 fw-bold text-dark">
                                                {freeze.days} {freeze.days === 1 ? "dia" : "dias"}
                                                {refunded > 0 && (
                                                    <span className="text-success ms-1 fw-normal font-size-10">
                                                        (+{refunded} devolvidos)
                                                    </span>
                                                )}
                                            </span>
                                        </div>

                                        {/* Linha 2: Datas */}
                                        <div className="d-flex flex-wrap gap-3 font-size-12 mb-1">
                                            <span className="text-muted">
                                                <i className="mdi mdi-calendar-start me-1 text-success"></i>
                                                {formatDate(freeze.startDate)}
                                            </span>
                                            <span className="text-muted">→</span>
                                            <span className="text-muted">
                                                <i className="mdi mdi-calendar-end me-1 text-danger"></i>
                                                {formatDate(displayEndDate)}
                                            </span>
                                        </div>
                                        {freeze.reason && (
                                            <div className="font-size-11 text-muted mt-1">
                                                <i className="mdi mdi-message-text-outline me-1"></i>{freeze.reason}
                                            </div>
                                        )}
                                        {freeze.attachmentUrl && (
                                            <div className="font-size-11 mt-1">
                                                <a
                                                    href={freeze.attachmentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="d-inline-flex align-items-center gap-1 text-decoration-none fw-semibold"
                                                    style={{ color: 'var(--color-brand-accent)' }}
                                                >
                                                    <i className="mdi mdi-paperclip font-size-14"></i>
                                                    Ver Atestado ({freeze.attachmentName || "Visualizar"})
                                                </a>
                                            </div>
                                        )}
                                        {freeze.createdBy && (
                                            <div className="mt-2 pt-2 border-top d-flex flex-wrap gap-3 font-size-11 text-muted">
                                                <span>
                                                    <i className="mdi mdi-account-outline me-1"></i>
                                                    {freeze.createdBy.name || "—"}
                                                </span>
                                                {freeze.createdBy.email && (
                                                    <span>
                                                        <i className="mdi mdi-email-outline me-1"></i>
                                                        {freeze.createdBy.email}
                                                    </span>
                                                )}
                                                {freeze.createdBy.phone && (
                                                    <span>
                                                        <i className="mdi mdi-phone-outline me-1"></i>
                                                        {freeze.createdBy.phone}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Collapse>
                </div>
            )}
        </div>
    );
};

export default ActionSuspend;
